// Brauzerda fayl yuklab olish — CSV eksport uchun.
//
// NEGA BIR JOYDA: `DataTable` eksportni umumiy komponent sifatida beradi,
// har feature o'zi CSV yozmaydi — formatlash (qochirish, BOM) bir marta
// to'g'ri yozilib, hamma joyda ishlatiladi.

import { currencyExponent, type Money } from './money'

/** Blob uchun brauzer yuklashini boshlaydi */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/**
 * CSV maydonini xavfsiz qochirish.
 *
 * NEGA `'` PREFIKSI: `=`, `+`, `-`, `@` bilan boshlanadigan qiymatni Excel
 * formula deb bajaradi (CSV injection). Abonent login yoki izohi CSV'ga
 * to'g'ridan-to'g'ri tushadi, shuning uchun bu MAJBURIY himoya.
 */
function csvCell(value: unknown): string {
  if (value === null || value === undefined) return ''
  // `String(obj)` "[object Object]" berardi — bunday qiymat CSV'da ma'nosiz,
  // shuning uchun JSON'ga, qolganlari (son, mantiqiy, satr) esa oddiy
  // satrga aylantiriladi. `typeof === 'object'` bilan ajratilgandan keyin
  // ham qolgan tur TypeScript uchun hamon `unknown` bo'lib ko'rinadi
  // (bu holatni til aniq tora olmaydi), shuning uchun qoida shu bitta
  // qatorda o'chirilgan — natija baribir xavfsiz: primitivlar uchun
  // `String()` standart va kutilgan ishlaydi.
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  const raw = typeof value === 'object' ? JSON.stringify(value) : String(value)
  const guarded = /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw
  return `"${guarded.replace(/"/g, '""')}"`
}

export interface CsvColumn<T> {
  header: string
  value: (row: T) => unknown
}

/**
 * MINOR summani CSV uchun MAJOR birlikdagi matnga: 2500000 AFN → "25000.00".
 *
 * <h2>⚠️ Nega kerak bo'lib qoldi</h2>
 * Abonentlar jadvalida ekranda `formatMoneyValue` ("25 000 afg'oni")
 * ko'rinardi, CSV'ga esa AYNAN SHU ustun `row.balance.amount`, ya'ni XOM
 * minor son (2500000) tushardi. Bitta jadval, bitta sarlavha — ikki xil
 * kattalik, farqi 100 barobar. Buxgalter faylni ochib "balans 2 500 000"
 * deb o'qishi uchun hech qanday ogohlantirish yo'q edi.
 *
 * <h2>Nega aynan shu shakl</h2>
 * Backend hisobot eksporti (`report/export/CsvWriter.java#money`) minor
 * summani `BigDecimal.valueOf(amount, digits).toPlainString()` bilan
 * yozadi. Ikkala eksport bitta tizimniki va bitta jadvalda uchrashishi
 * mumkin — shakl AYNAN bir xil bo'lishi kerak, aks holda ustunlarni
 * qo'shganda tur mos kelmaydi.
 *
 * <h2>Nega bo'lish emas, satr amallari</h2>
 * `amount / 100` suzuvchi nuqta beradi: 1999 / 100 = 19.99 ko'rinadi,
 * lekin `(0.1 + 0.2) * 100` allaqachon 30.000000000000004. Butun sonni
 * satr sifatida kesish esa AYNIQ — yaxlitlash umuman yuz bermaydi.
 *
 * <p>⚠️ Kasr xonalari valyutadan olinadi (`currencyExponent`), qattiq
 * "2" emas: JPY da kasr yo'q va 5000 JPY ni "50.00" deb yozish summani
 * 100 barobar kichraytirardi.
 *
 * <p>Valyuta kodi bu yerda ATAYLAB yo'q — u alohida ustun bo'ladi.
 * "25000.00 AFN" bitta katakda bo'lsa Excel uni son sifatida o'qimaydi
 * va yig'indi ololmaydi, ya'ni eksportning asosiy ma'nosi yo'qoladi.
 */
export function csvMoney(value: Money | null | undefined): string {
  if (!value || !Number.isFinite(value.amount)) return ''
  const digits = currencyExponent(value.currency)
  const sign = value.amount < 0 ? '-' : ''
  const abs = Math.abs(Math.trunc(value.amount)).toString()
  if (digits === 0) return `${sign}${abs}`
  const padded = abs.padStart(digits + 1, '0')
  return `${sign}${padded.slice(0, -digits)}.${padded.slice(-digits)}`
}

// UTF-8 BOM (U+FEFF) — busiz Excel faylni ANSI deb o'qib, kirill/lotin
// harflarini buzadi.
const UTF8_BOM = '\uFEFF'

/** Ro'yxatni CSV matniga aylantiradi (BOM bilan — Excel UTF-8'ni to'g'ri o'qiydi) */
export function toCsv<T>(rows: readonly T[], columns: readonly CsvColumn<T>[]): string {
  const head = columns.map((c) => csvCell(c.header)).join(';')
  const body = rows.map((row) => columns.map((c) => csvCell(c.value(row))).join(';')).join('\r\n')
  return `${UTF8_BOM}${head}\r\n${body}`
}

export function downloadCsv<T>(
  filename: string,
  rows: readonly T[],
  columns: readonly CsvColumn<T>[],
): void {
  const blob = new Blob([toCsv(rows, columns)], { type: 'text/csv;charset=utf-8' })
  downloadBlob(blob, filename.endsWith('.csv') ? filename : `${filename}.csv`)
}
